/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import * as dto from 'client/dto';

import { BdLocationVulnerabilityIcon } from 'components/_common/Icon/BdLocationVulnerabilityIcon';
import { Typography } from 'components/_common/Typography';

import bepiSelectors from 'bepi/bepi.selectors';
import BepiDisclaimer from 'bepi/components/BepiDisclaimer';
import BepiScoreBar from 'bepi/components/BepiScoreBar';
import BepiVulnerabilityStatement from 'bepi/components/BepiVulnerabilityStatement';
import locationsSelectors from 'locations/locations.selectors';

type RiskOverviewBepiExportContainerProps = SxProp & {
  locationId: number;
  showDisruptionTitle?: boolean;
};

const RiskOverviewBepiExportContainer: React.FC<RiskOverviewBepiExportContainerProps> = ({
  locationId,
  showDisruptionTitle = false,
  ...props
}) => {
  const locationResiliency = useSelector(
    bepiSelectors.getVulnerabilityIndexByLocationId(locationId)
  );
  const bepiLocationName = useSelector(bepiSelectors.getBepiLocationName(locationId));
  const locationName = useSelector(locationsSelectors.getLocationName(locationId));
  const bepiValues = useSelector(bepiSelectors.bepiValuesByLocationId(locationId));

  return (
    <div {...props}>
      <Typography variant="h6" color="stone90">
        {showDisruptionTitle && 'Risk of Disruption – '}Disease Risk Vulnerability in&nbsp;
        {locationName}
      </Typography>
      <div
        className="bepi-stats"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          columnGap: '32px',
          rowGap: '32px',
          mb: '8px',
          mt: '16px',
        }}
      >
        <div className="icon-container" sx={{ gridColumn: '1' }}>
          <BdLocationVulnerabilityIcon
            locationResiliency={locationResiliency}
            locationName={bepiLocationName}
          />
        </div>
        <div className="vulnerability-statement-container" sx={{ gridColumn: '2 / span 2' }}>
          <BepiVulnerabilityStatement
            bepiLocationName={bepiLocationName}
            locationResiliency={locationResiliency}
          />
        </div>
        <div sx={{ gridColumn: '1' }}>
          <BepiScoreBar
            category={dto.BepiCategory.Demographics}
            value={bepiValues?.[dto.BepiCategory.Demographics]}
          />
          <BepiScoreBar
            category={dto.BepiCategory.Healthcare}
            value={bepiValues?.[dto.BepiCategory.Healthcare]}
          />
          <BepiScoreBar
            category={dto.BepiCategory.DiseaseDynamics}
            value={bepiValues?.[dto.BepiCategory.DiseaseDynamics]}
          />
        </div>
        <div sx={{ gridColumn: '2' }}>
          <BepiScoreBar
            category={dto.BepiCategory.PoliticalDomestic}
            value={bepiValues?.[dto.BepiCategory.PoliticalDomestic]}
          />
          <BepiScoreBar
            category={dto.BepiCategory.PoliticalInternational}
            value={bepiValues?.[dto.BepiCategory.PoliticalInternational]}
          />
        </div>
        <div sx={{ gridColumn: '3' }}>
          <BepiScoreBar
            category={dto.BepiCategory.PublicHealth}
            value={bepiValues?.[dto.BepiCategory.PublicHealth]}
          />
          <BepiScoreBar
            category={dto.BepiCategory.Economy}
            value={bepiValues?.[dto.BepiCategory.Economy]}
          />
        </div>
      </div>
      <BepiDisclaimer />
    </div>
  );
};

export default RiskOverviewBepiExportContainer;
